import type {Instance} from 'tippy.js';
import {createTippy} from '../modules/tippy.ts';

/**
 * tooltip web component
 *
 * Usage:
 * <tippy-tooltip data-tippy-placement="top">
 *  <div>
 *    <p>Icon</p>
 *  </div>
 *  <div data-tippy-html-content style="display: none;">
 *    <p>Tooltip content</p>
 *  </div>
 * </tippy-tooltip>
 *
 * * The `data-tippy-placement` attribute is optional and must be one of
 * https://atomiks.github.io/tippyjs/v6/all-props/#placement
 *
 * * The `data-tippy-html-content` attribute is required and must be a hidden element containing the
 * tooltip content. Note that the element must be hidden and when used as the tooltip content the
 * display will be set to block.
 */
class TippyTooltip extends HTMLElement {
  #tippyInstance: any;

  connectedCallback() {
    new MutationObserver(() => {
      this.setupTippy();
    }).observe(this, {childList: true});
    this.setupTippy();
  }

  // Make sure manually controlled tooltips are hidden when component is unmounted.
  disconnectedCallback() {
    if (this.hasAttribute('data-tippy-stay-until-dismiss')) {
      this.#tippyInstance?.hide();
    }
  }

  setupTippy() {
    if (this.#tippyInstance) {
      this.#tippyInstance.destroy();
    }

    const options: any = {
      theme: 'tooltip',
      role: 'tooltip',
      interactive: true,
    };

    if (this.hasAttribute('data-tippy-placement')) {
      options.placement = this.getAttribute('data-tippy-placement');
    }

    if (this.hasAttribute('data-tippy-show-on-create')) {
      options.showOnCreate = true;
    }

    if (this.hasAttribute('data-tippy-stay-until-dismiss')) {
      options.trigger = 'manual';
      options.onShow = (instance: Instance) => {
        const popper = instance.popper;
        const handler = (e: MouseEvent) => {
          if ((e.target as Element).closest('[data-tippy-dismiss]')) {
            e.preventDefault();
            instance.hide();
            this.dispatchEvent(new CustomEvent('tippy-dismiss'));
            popper.removeEventListener('click', handler);
          }
        };
        popper.addEventListener('click', handler);
      };
    }

    if (this.hasAttribute('data-tippy-delay')) {
      const delay = Number(this.getAttribute('data-tippy-delay'));
      options.delay = Number.isFinite(delay) ? [delay, 0] : 0;
    }

    const content: HTMLElement = this.querySelector('[data-tippy-html-content]');
    if (content) {
      const cloned = content.cloneNode(true) as HTMLElement;
      cloned.style.display = 'block';
      options.content = cloned;
    }

    this.#tippyInstance = createTippy(this, options);
  }
}

customElements.define('tippy-tooltip', TippyTooltip);
